/**
 * Hook for fetching issue or Wikipedia preview data
 * 
 * This hook provides functionality to:
 * 1. Check if a Wikipedia page exists as an issue in our database (exact match)
 * 2. Fetch Wikipedia summary if no issue exists
 * 3. Return unified preview data for display
 */

import { useState, useCallback } from "react";
import api from "config/axiosConfig";
import { Issue } from "util/constants";
import { getWikipediaSummary } from "util/wikiUtils";
import { apiRoutes } from "util/apiRoutes";

/**
 * Preview data type - can be either an existing issue or Wikipedia data
 */
export type PreviewData = 
  | { type: "issue"; data: Issue }
  | { type: "wikipedia"; data: WikipediaPreviewData };

/**
 * Wikipedia preview data structure
 */
export interface WikipediaPreviewData {
  title: string;
  displayTitle: string;
  canonicalTitle: string;
  extract: string;
  description: string | null;
  thumbnail: string | null;
  wikipediaUrl: string;
  mobileUrl: string;
}

/**
 * Hook return type
 */
export interface UseIssuePreviewReturn {
  /** Current preview data */
  previewData: PreviewData | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Fetch preview data by Wikipedia title or URL */
  fetchPreview: (wikipediaTitleOrUrl: string) => Promise<void>;
  /** Clear current preview data */
  clearPreview: () => void;
}

/**
 * Extract Wikipedia page title from URL
 * @param urlOrTitle - Wikipedia URL or plain title
 * @returns Extracted title or original string if not a URL
 */
export const extractWikipediaTitle = (urlOrTitle: string): string => {
  try {
    // Check if it's a URL
    if (urlOrTitle.includes("wikipedia.org/wiki/")) {
      const url = new URL(urlOrTitle.startsWith("http") ? urlOrTitle : `https://${urlOrTitle}`);
      const pathParts = url.pathname.split("/wiki/");
      if (pathParts.length >= 2 && pathParts[1]) {
        return decodeURIComponent(pathParts[1]);
      }
    }
    // Return as-is if not a URL
    return urlOrTitle;
  } catch (error) {
    // If URL parsing fails, return original string
    return urlOrTitle;
  }
};

/**
 * Check if a URL is a Wikipedia link
 * @param url - URL to check
 * @returns true if URL is a Wikipedia link
 */
export const isWikipediaUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObj.hostname.includes("wikipedia.org") && urlObj.pathname.includes("/wiki/");
  } catch {
    return false;
  }
};

/**
 * Custom hook for fetching issue or Wikipedia preview data
 */
export const useIssuePreview = (): UseIssuePreviewReturn => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch preview data by Wikipedia title or URL
   */
  const fetchPreview = useCallback(async (wikipediaTitleOrUrl: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Extract title from URL if needed
      const title = extractWikipediaTitle(wikipediaTitleOrUrl);
      
      // Clean the title (replace spaces with underscores for canonical name)
      const canonicalName = title.replace(/ /g, "_");

      // 1. Try to find exact match in our issues database
      try {
        const issueResponse = await api.get(apiRoutes.issues.byName(encodeURIComponent(canonicalName)));
        
        if (issueResponse.status === 200 && issueResponse.data?.id) {
          setPreviewData({
            type: "issue",
            data: issueResponse.data as Issue
          });
          setIsLoading(false);
          return;
        }
      } catch (issueError: any) {
        // Issue not found, continue to Wikipedia
        if (issueError.response?.status !== 404) {
          console.warn("Error checking for existing issue:", issueError);
        }
      }

      // 2. If no issue found, fetch from Wikipedia
      const wikiSummary = await getWikipediaSummary(title);
      
      if (!wikiSummary) {
        throw new Error("Wikipedia page not found or not available");
      }

      // Build preview data from Wikipedia summary
      const wikipediaPreview: WikipediaPreviewData = {
        title: wikiSummary.title,
        displayTitle: wikiSummary.displayTitle,
        canonicalTitle: wikiSummary.canonicalTitle,
        extract: wikiSummary.extract,
        description: wikiSummary.description,
        thumbnail: wikiSummary.thumbnail,
        wikipediaUrl: wikiSummary.wikipediaUrl,
        mobileUrl: typeof wikiSummary.contentUrls?.mobile?.page === "string"
          ? wikiSummary.contentUrls.mobile.page
          : wikiSummary.wikipediaUrl
      };

      setPreviewData({
        type: "wikipedia",
        data: wikipediaPreview
      });
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error("Failed to fetch preview");
      setError(errorObj);
      console.error("Error fetching preview:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Clear current preview data
   */
  const clearPreview = useCallback(() => {
    setPreviewData(null);
    setError(null);
  }, []);

  return {
    previewData,
    isLoading,
    error,
    fetchPreview,
    clearPreview
  };
};

